/**
 * Non-blocking banner that surfaces a server-side AI quality review of
 * the currently-open saved dashboard. Renders above the spec content
 * (below the toolbar), shows score + top issues, and lets the user
 * dismiss it for the session.
 *
 * Behavior:
 * - First render: shows "Granska kvalitet med AI" CTA. The review is
 *   on-demand — we don't auto-fire it on every dashboard open to keep
 *   cost predictable; the user opts in.
 * - Click → POST /api/dashboards/{id}/quality-review → banner expands
 *   with score chip, summary line, and a collapsible list of issues.
 * - Each issue shows severity badge + category + message, and (if the
 *   reviewer included one) a one-line suggested fix.
 * - Dismiss button hides the banner for this session via a small
 *   session-store; next dashboard open re-shows the CTA.
 */

import { useEffect, useState } from "react"

import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Loader2,
  PlusSquare,
  Rocket,
  Sparkles,
  Wand2,
  X,
} from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { SavedDashboard } from "../services/dashboard-service"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import {
  isSelfImproveTimeout,
  SELF_IMPROVE_MAX_ITERATIONS,
} from "@/features/dash/utils/quality-review"

type Severity = "high" | "medium" | "low"

interface QualityIssue {
  severity: Severity
  category: string
  message: string
  element_path?: string | null
  suggested_fix?: string | null
}

interface QualityReview {
  score: number
  summary: string
  issues: QualityIssue[]
  reviewed_model?: string | null
  reviewed_at?: string | null
  suggested_patch?: Array<Record<string, unknown>>
}

interface QualityHistoryEntry {
  score: number
  summary: string
  reviewed_at?: string | null
}

const SEVERITY_STYLES: Record<Severity, { badge: string; dot: string }> = {
  high: {
    badge: "bg-destructive/10 text-destructive border-destructive/20",
    dot: "bg-destructive",
  },
  medium: {
    badge: "bg-warning/10 text-warning-foreground border-warning/30",
    dot: "bg-warning",
  },
  low: {
    badge: "bg-muted text-muted-foreground border-border",
    dot: "bg-muted-foreground",
  },
}

function scoreChipClasses(score: number): string {
  if (score >= 8) return "bg-success/15 text-success border-success/30"
  if (score >= 5) return "bg-warning/15 text-warning-foreground border-warning/30"
  return "bg-destructive/15 text-destructive border-destructive/30"
}

export function QualityReviewBanner({
  dashboardId,
  initialReview,
  history,
  canAutoGenerateMissingTiles = true,
  onDashboardRefreshed,
}: {
  dashboardId: string
  /** Last review persisted on the doc. When provided, we render the
   *  loaded state immediately without forcing the user to click CTA. */
  initialReview?: QualityReview | null
  /** Append-only history snapshots for the sparkline. */
  history?: QualityHistoryEntry[]
  /** The server-side bulk generator currently requires a live query binding. */
  canAutoGenerateMissingTiles?: boolean
  /** Called after a fix endpoint returns an updated dashboard envelope.
   *  Parent uses this to push the new spec into the store WITHOUT a
   *  full page reload — reload drops to the landing page because the
   *  open dashboard isn't deep-linked in the URL. */
  onDashboardRefreshed?: (updated: SavedDashboard) => void
}) {
  const { i18n, t } = useTranslation()
  const requestedLanguage = (i18n.resolvedLanguage ?? i18n.language).split("-")[0].toLowerCase()
  const language = ["de", "en", "fi", "no", "sv"].includes(requestedLanguage)
    ? requestedLanguage
    : "en"
  const [review, setReview] = useState<QualityReview | null>(initialReview ?? null)
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [regeneratingSql, setRegeneratingSql] = useState(false)
  const [addingTiles, setAddingTiles] = useState(false)
  const [selfImproving, setSelfImproving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [applyOutcome, setApplyOutcome] = useState<string | null>(null)

  // The parent replaces both the spec and persisted review after every
  // improvement. Keep this local banner state in lockstep so resolved
  // findings disappear without a manual refresh or edit-mode round-trip.
  useEffect(() => {
    setReview(initialReview ?? null)
    setDismissed(false)
  }, [dashboardId, initialReview])

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasSqlIssues = !!review?.issues?.some((i) => i.category === "sql_correctness")
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const missingTileCount = review?.issues?.filter((i) => i.category === "missing_tile").length ?? 0

  if (dismissed) return null

  async function runReview() {
    setLoading(true)
    setError(null)
    setApplyOutcome(null)
    try {
      const res = await api.post<unknown, QualityReview>(
        `/dashboards/${dashboardId}/quality-review`,
        { language }
      )
      setReview(res)
      setExpanded(true)
      // The review response is intentionally slim, while the append-only
      // history is persisted on the dashboard envelope. Re-read that envelope
      // so the second review immediately supplies two points to the trend
      // sparkline instead of requiring a page reload.
      try {
        const updated = await api.get<never, SavedDashboard>(`/dashboards/${dashboardId}`)
        onDashboardRefreshed?.(updated)
      } catch (historyError) {
        logger.warn("Quality review history refresh failed", historyError as Error)
      }
    } catch (err) {
      logger.warn("Quality review failed", err as Error)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setError(String((err as Error)?.message ?? err))
    } finally {
      setLoading(false)
    }
  }

  async function refreshReviewAfterMutation(
    updated: SavedDashboard
  ): Promise<QualityReview | null> {
    // Show the new spec immediately and remove the stale findings while the
    // follow-up review runs. The review endpoint persists its fresh result.
    const clearedReview = { ...updated, quality_review: null }
    setReview(null)
    onDashboardRefreshed?.(clearedReview)

    try {
      const freshReview = await api.post<unknown, QualityReview>(
        `/dashboards/${dashboardId}/quality-review`,
        { language }
      )
      const nextHistory = [
        ...(updated.quality_history ?? []),
        {
          score: freshReview.score,
          summary: freshReview.summary,
          reviewed_at: freshReview.reviewed_at,
          reviewed_model: freshReview.reviewed_model,
        },
      ].slice(-20)
      setReview(freshReview)
      setExpanded(true)
      onDashboardRefreshed?.({
        ...updated,
        quality_review: freshReview,
        quality_history: nextHistory,
      })
      return freshReview
    } catch (err) {
      // The mutation already succeeded and is visible. A review failure must
      // not roll it back or leave the old, now-invalid issue list on screen.
      logger.warn("Quality re-review after dashboard mutation failed", err as Error)
      return null
    }
  }
  async function selfImprove() {
    if (selfImproving) return
    setSelfImproving(true)
    setApplyOutcome(null)
    setError(null)
    try {
      // The /self-improve route returns the full dashboard envelope
      // (same shape as a GET /api/dashboards/{id}) plus trajectory
      // fields. Cast loosely so we can pass the dashboard portion to
      // the parent's loadDashboard callback below.
      const res = await api.post<
        unknown,
        SavedDashboard & {
          trajectory?: Array<{
            score: number
            patches_applied?: number
            sql_regenerated?: number
            tiles_added?: number
          }>
          final_score?: number
          iterations?: number
        }
      >(`/dashboards/${dashboardId}/self-improve`, {
        target_score: 8,
        max_iterations: SELF_IMPROVE_MAX_ITERATIONS,
        language,
      })
      const traj = res.trajectory ?? []
      // The route's last score is measured before that iteration's fixes.
      // Re-review once after the bounded mutation so the updated dashboard
      // and resolved findings become visible without a manual refresh.
      const freshReview = await refreshReviewAfterMutation(res)
      const final = freshReview?.score ?? res.final_score
      const totalPatches = traj.reduce((s, t) => s + (t.patches_applied ?? 0), 0)
      const totalSql = traj.reduce((s, t) => s + (t.sql_regenerated ?? 0), 0)
      const totalTiles = traj.reduce((s, t) => s + (t.tiles_added ?? 0), 0)
      const initial = review?.score
      const changed = totalPatches + totalSql + totalTiles > 0
      const improved = typeof initial === "number" && typeof final === "number" && final > initial
      const outcomeKeyBase =
        improved || changed
          ? "dash.quality_review.self_improve_outcome"
          : "dash.quality_review.self_improve_noop_outcome"
      const outcomeKey = `${outcomeKeyBase}_${traj.length === 1 ? "one" : "other"}`
      setApplyOutcome(
        t(outcomeKey, {
          defaultValue:
            improved || changed
              ? `Körningen slutade på {{final}}/10 efter {{count}} ${traj.length === 1 ? "runda" : "rundor"}. Patches: {{p}}, SQL: {{s}}, Nya tiles: {{tl}}`
              : `Ingen automatisk förbättring kunde appliceras. Slutscore: {{final}}/10 efter {{count}} ${traj.length === 1 ? "runda" : "rundor"}.`,
          final: final ?? "?",
          count: traj.length,
          p: totalPatches,
          s: totalSql,
          tl: totalTiles,
        })
      )
    } catch (err) {
      logger.warn("Self-improve failed", err as Error)
      setError(
        isSelfImproveTimeout(err)
          ? t("dash.quality_review.self_improve_timeout")
          : t("dash.quality_review.self_improve_error")
      )
    } finally {
      setSelfImproving(false)
    }
  }

  async function autoFixMissingTiles() {
    if (addingTiles) return
    setAddingTiles(true)
    setApplyOutcome(null)
    setError(null)
    try {
      const res = await api.post<
        unknown,
        SavedDashboard & {
          added?: number
          failed_sql?: number
          skipped?: number
          results?: Array<{ issue?: string; status: string; error?: string }>
        }
      >(`/dashboards/${dashboardId}/auto-fix-missing-tiles`, {})
      const added = res.added ?? 0
      const failedSql = res.failed_sql ?? 0
      const skipped = res.skipped ?? 0
      const resultErrors = (res.results ?? []).map((result) => result.error ?? "")
      const allSkippedNoConnection =
        added === 0 &&
        skipped > 0 &&
        resultErrors.some((reason) => reason.includes("no connection_id"))
      const allSkippedNoSchema =
        added === 0 &&
        skipped > 0 &&
        resultErrors.some((reason) => reason.includes("no cached schema"))
      if (allSkippedNoConnection) {
        setApplyOutcome(t("dash.quality_review.tiles_skipped_no_connection"))
      } else if (allSkippedNoSchema) {
        setApplyOutcome(t("dash.quality_review.tiles_skipped_no_schema"))
      } else {
        setApplyOutcome(
          t("dash.quality_review.tiles_added_outcome", {
            defaultValue:
              "Tiles tillagda: {{added}} (med fel: {{failed}}, hoppade över: {{skipped}})",
            added,
            failed: failedSql,
            skipped,
          })
        )
      }
      if (added > 0 || failedSql > 0) await refreshReviewAfterMutation(res)
    } catch (err) {
      logger.warn("Auto-fix missing tiles failed", err as Error)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setError(String((err as Error)?.message ?? err))
    } finally {
      setAddingTiles(false)
    }
  }

  async function regenerateFailedSql() {
    if (regeneratingSql) return
    setRegeneratingSql(true)
    setApplyOutcome(null)
    setError(null)
    try {
      const res = await api.post<
        unknown,
        SavedDashboard & {
          regenerated?: number
          still_failed?: number
          skipped?: number
          results?: Array<{ binding_id: string; status: string; error?: string }>
        }
      >(`/dashboards/${dashboardId}/regenerate-failed-bindings`, {})
      const regen = res.regenerated ?? 0
      const stillFailed = res.still_failed ?? 0
      const skipped = res.skipped ?? 0
      setApplyOutcome(
        t("dash.quality_review.sql_regen_outcome", {
          defaultValue:
            "SQL regenererat: {{fixed}} fixade, {{still}} kvarstår, {{skipped}} kunde inte regenereras",
          fixed: regen - stillFailed,
          still: stillFailed,
          skipped,
        })
      )
      if (regen > 0) await refreshReviewAfterMutation(res)
    } catch (err) {
      logger.warn("SQL regeneration failed", err as Error)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setError(String((err as Error)?.message ?? err))
    } finally {
      setRegeneratingSql(false)
    }
  }

  async function applyFixes() {
    if (!review?.suggested_patch?.length || applying) return
    setApplying(true)
    setApplyOutcome(null)
    setError(null)
    try {
      const res = await api.post<
        { patch: Array<Record<string, unknown>> },
        SavedDashboard & {
          applied: number
          skipped?: unknown[]
          errors?: unknown[]
        }
      >(`/dashboards/${dashboardId}/apply-quality-fixes`, {
        patch: review.suggested_patch,
      })
      setApplyOutcome(
        res.applied > 0
          ? t("dash.quality_review.applied_ok", {
              defaultValue: "Tillämpade {{n}} förbättringar",
              n: res.applied,
            })
          : t("dash.quality_review.applied_zero", "Inga förbättringar kunde tillämpas.")
      )
      if (res.applied > 0) await refreshReviewAfterMutation(res)
    } catch (err) {
      logger.warn("Apply quality fixes failed", err as Error)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      setError(String((err as Error)?.message ?? err))
    } finally {
      setApplying(false)
    }
  }

  // Initial state — show CTA chip
  if (!review) {
    return (
      <div className="border-border bg-card/50 mb-3 flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
        <div className="text-muted-foreground flex items-center gap-2">
          <Sparkles className="text-primary size-4" />
          <span>
            {t(
              "dash.quality_review.cta_hint",
              "Vill du att en AI granskar dashboarden och föreslår förbättringar?"
            )}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button disabled={loading} size="sm" variant="default" onClick={runReview}>
            {loading ? (
              <>
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                {t("dash.quality_review.running", "Granskar...")}
              </>
            ) : (
              <>
                <Sparkles className="mr-1.5 size-3.5" />
                {t("dash.quality_review.cta", "Granska kvalitet")}
              </>
            )}
          </Button>
          <Button
            aria-label={t("common.dismiss", "Stäng")}
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
          >
            <X className="size-3.5" />
          </Button>
        </div>
        {error && <p className="text-destructive text-xs">{error}</p>}
      </div>
    )
  }

  // Review loaded — show score + issues
  return (
    <div className="border-border bg-card/50 mb-3 rounded-md border">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          className="flex flex-1 items-center gap-2 text-left text-sm"
          type="button"
          onClick={() => setExpanded((v) => !v)}
        >
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-xs",
              scoreChipClasses(review.score)
            )}
          >
            {review.score >= 8 ? (
              <CheckCircle2 className="size-3" />
            ) : (
              <AlertTriangle className="size-3" />
            )}
            {review.score}/10
          </span>
          <span className="text-foreground font-medium">
            {t("dash.quality_review.label", "AI-granskning")}:
          </span>
          <span className="text-muted-foreground">{review.summary}</span>
        </button>
        <div className="flex items-center gap-1">
          {/* One-click self-improve — runs one bounded review + fix pass,
              followed by a fresh review. The user can run another pass
              when the score remains below 8. */}
          {review.score < 8 &&
            ((review.suggested_patch?.length ?? 0) > 0 || hasSqlIssues || missingTileCount > 0) && (
              <Button
                disabled={selfImproving || applying || regeneratingSql || addingTiles || loading}
                size="sm"
                variant="default"
                onClick={selfImprove}
              >
                {selfImproving ? (
                  <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                ) : (
                  <Rocket className="mr-1.5 size-3.5" />
                )}
                {t("dash.quality_review.self_improve", "Förbättra automatiskt")}
              </Button>
            )}
          {/* Apply-fixes button — only when reviewer produced a non-empty patch */}
          {review.suggested_patch && review.suggested_patch.length > 0 && (
            <Button
              disabled={applying || loading || regeneratingSql || selfImproving}
              size="sm"
              variant="default"
              onClick={applyFixes}
            >
              {applying ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 size-3.5" />
              )}
              {t("dash.quality_review.apply", {
                defaultValue: "Tillämpa {{n}} förbättringar",
                n: review.suggested_patch.length,
              })}
            </Button>
          )}
          {/* Regenerate-failed-SQL button — only when reviewer flagged
              sql_correctness issues. Patch-based apply can't fix SQL;
              this button calls the LLM-driven SQL repair endpoint. */}
          {hasSqlIssues && (
            <Button
              disabled={regeneratingSql || applying || addingTiles || loading}
              size="sm"
              variant="secondary"
              onClick={regenerateFailedSql}
            >
              {regeneratingSql ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Database className="mr-1.5 size-3.5" />
              )}
              {t("dash.quality_review.fix_sql", "Fixa SQL-fel")}
            </Button>
          )}
          {/* Auto-fix missing tiles — reviewer flagged gaps that need
              new tile insertion (e.g. missing YoY). Variant C path:
              service generates a complete tile with inline SQL, runs
              it, and appends to the dashboard. */}
          {missingTileCount > 0 && canAutoGenerateMissingTiles && (
            <Button
              disabled={addingTiles || applying || regeneratingSql || loading}
              size="sm"
              variant="secondary"
              onClick={autoFixMissingTiles}
            >
              {addingTiles ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <PlusSquare className="mr-1.5 size-3.5" />
              )}
              {t("dash.quality_review.add_tiles", {
                defaultValue: "Lägg till {{n}} saknade tiles",
                n: missingTileCount,
              })}
            </Button>
          )}
          {missingTileCount > 0 && !canAutoGenerateMissingTiles && (
            <span
              className="text-muted-foreground max-w-64 text-xs"
              title={t("dash.quality_review.add_tiles_requires_connection")}
            >
              {t("dash.quality_review.add_tiles_file_hint")}
            </span>
          )}
          <Button disabled={loading} size="sm" variant="ghost" onClick={runReview}>
            {loading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              t("dash.quality_review.rerun", "Granska igen")
            )}
          </Button>
          <Button
            aria-label={t("common.dismiss", "Stäng")}
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>
      {applyOutcome && (
        <p className="text-muted-foreground border-border border-t px-3 py-1.5 text-xs">
          {applyOutcome}
        </p>
      )}
      {error && (
        <p className="text-destructive border-border border-t px-3 py-1.5 text-xs">{error}</p>
      )}
      {/* Sparkline of score-over-time, hidden when we have ≤ 1 entry */}
      {history && history.length >= 2 && expanded && <ScoreSparkline history={history} />}
      {expanded && review.issues.length > 0 && (
        <ul className="border-border space-y-2 border-t px-3 py-2 text-xs">
          {review.issues.map((issue, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span
                className={cn(
                  "mt-1.5 inline-block size-2 shrink-0 rounded-full",
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  SEVERITY_STYLES[issue.severity]?.dot
                )}
              />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className={cn(
                      "inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase",
                      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                      SEVERITY_STYLES[issue.severity]?.badge
                    )}
                  >
                    {t(`dash.quality_review.severity.${issue.severity}`, {
                      defaultValue: issue.severity,
                    })}
                  </span>
                  <span className="text-muted-foreground text-[11px]">
                    {t(`dash.quality_review.category.${issue.category}`, {
                      defaultValue: issue.category.replaceAll("_", " "),
                    })}
                  </span>
                  {issue.element_path && (
                    <code className="text-muted-foreground text-[10px]">{issue.element_path}</code>
                  )}
                </div>
                <p className="text-foreground mt-0.5">{issue.message}</p>
                {issue.suggested_fix && (
                  <p className="text-muted-foreground mt-0.5 italic">→ {issue.suggested_fix}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {expanded && review.issues.length === 0 && (
        <p className="border-border text-success border-t px-3 py-2 text-xs">
          {t(
            "dash.quality_review.no_issues",
            "Inga konkreta förbättringar att föreslå — ser bra ut!"
          )}
        </p>
      )}
    </div>
  )
}

/**
 * Tiny inline SVG sparkline of score-over-time. Pure SVG, no recharts
 * — keeps the banner light. Y-axis is 0-10 (fixed), x-axis is the
 * history entries in chronological order. The latest entry is dotted
 * to draw the eye to "current state vs trend".
 */
function ScoreSparkline({ history }: { history: QualityHistoryEntry[] }) {
  const { t } = useTranslation()
  const W = 280
  const H = 32
  const PAD_X = 4
  const PAD_Y = 4
  const points = history.slice(-12)
  if (points.length < 2) return null
  const dx = (W - 2 * PAD_X) / (points.length - 1)
  const scaleY = (s: number) => H - PAD_Y - (s / 10) * (H - 2 * PAD_Y)
  const polyline = points.map((p, i) => `${PAD_X + i * dx},${scaleY(p.score)}`).join(" ")
  const last = points[points.length - 1]

  const first = points[0]
  const delta = last.score - first.score
  return (
    <div className="border-border flex items-center gap-2 border-t px-3 py-2">
      <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
        {t("dash.quality_review.history", "Trend")}:
      </span>
      <svg aria-hidden="true" className="text-primary" height={H} width={W}>
        <polyline
          fill="none"
          points={polyline}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
        />
        <circle
          cx={PAD_X + (points.length - 1) * dx}
          cy={scaleY(last.score)}
          fill="currentColor"
          r={2.5}
        />
      </svg>
      <span className="text-muted-foreground text-[11px] tabular-nums">
        {first.score} → {last.score}
        {delta !== 0 && (
          <span className={cn("ml-1", delta > 0 ? "text-success" : "text-destructive")}>
            ({delta > 0 ? "+" : ""}
            {delta})
          </span>
        )}
      </span>
    </div>
  )
}
